/**
 * The fields a smart filter can name, read from the leads themselves
 * (`/api/v1/lead-fields`), one field's commonest values, and what a filter
 * list would have stopped. The routing engine filters on any key a lead
 * carries, so the filter editor offers what leads actually carry, not only
 * the defined properties.
 */
import { useQueries, useQuery } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components } from './schema';

type S = components['schemas'];
export type LeadField = S['LeadField'];
export type LeadFieldsResponse = S['LeadFieldsResponse'];
export type LeadFieldValue = S['LeadFieldValue'];
export type LeadFieldPreviewResponse = S['LeadFieldPreviewResponse'];

/** The campaigns as one stable key: deduplicated and sorted. */
function idsKey(campaignIds: string[] | undefined): string {
  return [...new Set((campaignIds ?? []).filter(Boolean))].sort().join(',');
}

/** Fields seen on recent leads of these campaigns (every campaign when none). */
export function useLeadFields(campaignIds: string[] | undefined, enabled = true) {
  const ids = idsKey(campaignIds);
  return useQuery({
    queryKey: ['lead-fields', ids],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/lead-fields', { params: { query: ids ? { campaign_ids: ids } : {} } }),
        'LeadFieldsResponse',
      ),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The routes still posting any of these fields, for the warning on the hidden
 * fields setting. Only asked for while there are fields to ask about.
 */
export function useHiddenFieldDelivery(
  keys: string[],
  on: { buyerId?: string; campaignId?: string },
) {
  const asked = [...keys].sort().join(',');
  return useQuery({
    queryKey: ['hidden-field-delivery', asked, on.buyerId ?? '', on.campaignId ?? ''],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/hidden-fields/delivery', {
          params: {
            query: {
              keys: asked,
              ...(on.buyerId ? { buyer_id: on.buyerId } : {}),
              ...(on.campaignId ? { campaign_id: on.campaignId } : {}),
            },
          },
        }),
        'HiddenFieldDeliveryResponse',
      ),
    enabled: asked !== '' && Boolean(on.buyerId || on.campaignId),
    staleTime: 60 * 1000,
  });
}

/** One field's commonest values on recent leads, for suggesting a value. */
export function useLeadFieldValues(field: string, campaignIds: string[] | undefined, enabled = true) {
  const ids = idsKey(campaignIds);
  const usable = /^[A-Za-z0-9_]{1,64}$/.test(field) && !field.startsWith('__');
  return useQuery({
    queryKey: ['lead-field-values', field, ids],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/lead-fields/values', {
          params: { query: { field, ...(ids ? { campaign_ids: ids } : {}) } },
        }),
        'LeadFieldValuesResponse',
      ),
    enabled: enabled && usable,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Several fields' commonest values at once, keyed by field. Shares its cache
 * with `useLeadFieldValues`, so a field read by either is read once.
 */
export function useLeadFieldValuesFor(fields: string[], campaignIds: string[] | undefined) {
  const ids = idsKey(campaignIds);
  const usable = fields.filter((f) => /^[A-Za-z0-9_]{1,64}$/.test(f) && !f.startsWith('__'));
  return useQueries({
    queries: usable.map((field) => ({
      queryKey: ['lead-field-values', field, ids],
      queryFn: async () =>
        unwrap(
          await api.GET('/api/v1/lead-fields/values', {
            params: { query: { field, ...(ids ? { campaign_ids: ids } : {}) } },
          }),
          'LeadFieldValuesResponse',
        ),
      staleTime: 5 * 60 * 1000,
    })),
    combine: (results) => {
      const out: Record<string, LeadFieldValue[]> = {};
      results.forEach((r, i) => {
        const field = usable[i];
        if (r.data && field) out[field] = r.data.values;
      });
      return out;
    },
  });
}

/**
 * How many of a campaign's recent leads `filters` would have stopped, run
 * through the routing evaluator on the server. Pass already-debounced
 * filters; `null` skips the call.
 */
export function useFilterPreview(campaignId: string | undefined, filters: unknown[] | null) {
  const body = JSON.stringify(filters ?? []);
  return useQuery({
    queryKey: ['lead-field-preview', campaignId ?? '', body],
    queryFn: async () =>
      unwrap(
        await api.POST('/api/v1/lead-fields/preview', {
          body: { campaign_id: campaignId ?? '', filters: filters ?? [] },
        }),
        'LeadFieldPreviewResponse',
      ),
    enabled: !!campaignId && filters !== null && filters.length > 0,
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

/**
 * How many rows each reference list holds, for the list picker. A hint beside
 * the name: the server answers with none when it cannot count.
 */
export function useReferenceListRows(enabled = true) {
  return useQuery({
    queryKey: ['lead-field-list-rows'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/lead-fields/list-rows', {}), 'ListRowsResponse'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * "17 Apr", or "17 Apr 2025" outside the current year: the day a key was
 * last seen, in UK time. The raw text when it is not a date.
 */
export function lastSeenLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const year = (x: Date) =>
    new Intl.DateTimeFormat('en-GB', { year: 'numeric', timeZone: 'Europe/London' }).format(x);
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(year(d) === year(now) ? {} : { year: 'numeric' }),
    timeZone: 'Europe/London',
  }).format(d);
}
